/**
 * ============================================================================
 * useRiskRankingQuery — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the bucketed risk-ranking view of every
 *          supplier. Server returns one envelope with four buckets
 *          (CRITICAL / MAJOR / MINOR / LOW) and per-bucket counts.
 * Endpoint: GET /api/v1/suppliers/risk-ranking?site={code}
 *
 * NOTE: a future task may add a polling cadence; for now the user pulls a
 *  fresh view by re-mounting the screen.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type { RiskCategory } from '../SupplierWorkbench/useSuppliersQuery';

export interface RiskRankingCard {
  supplier_id: string;
  name: string;
  criticality: 'CRITICAL' | 'MAJOR' | 'MINOR' | 'LOW';
  risk_score: number;
  last_assessment_at_utc: string | null;
}

export interface RiskRankingBucket {
  category: RiskCategory;
  count: number;
  cards: RiskRankingCard[];
}

export interface RiskRankingResponse {
  buckets: RiskRankingBucket[];
  /** Snapshot wall-clock used for the staleness hint. */
  generated_at_utc: string;
  /** Available site codes the user can filter by. */
  sites?: Array<{ code: string; label: string }>;
}

export interface RiskAssessmentDetail {
  assessment_id: string;
  ts_utc: string;
  formula_version: string;
  computed_score: number;
  category: RiskCategory;
  rationale: string;
}

export interface RiskAssessmentHistoryResponse {
  supplier_id: string;
  /** Most-recent first. */
  history: RiskAssessmentDetail[];
}

export function useRiskRankingQuery(
  siteCode?: string,
): UseQueryResult<RiskRankingResponse> {
  return useQuery<RiskRankingResponse>({
    queryKey: ['suppliers', 'risk-ranking', siteCode ?? '*'],
    queryFn: ({ signal }) =>
      apiFetch<RiskRankingResponse>('/api/v1/suppliers/risk-ranking', {
        query: { site: siteCode },
        signal,
      }),
  });
}

export function useRiskAssessmentHistoryQuery(
  supplierId: string | null,
): UseQueryResult<RiskAssessmentHistoryResponse> {
  return useQuery<RiskAssessmentHistoryResponse>({
    queryKey: ['suppliers', supplierId, 'risk-history'],
    enabled: !!supplierId,
    queryFn: ({ signal }) =>
      apiFetch<RiskAssessmentHistoryResponse>(
        `/api/v1/suppliers/${encodeURIComponent(supplierId!)}/risk-assessments`,
        { signal },
      ),
  });
}
